import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useKV } from "@/hooks/use-kv-adapter";
import { EmployeeProfileExtended } from "@/types/core-hr";
import { User, Phone, Envelope, House, Briefcase, IdentificationCard, Bank, Desktop, FileText, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

interface EmployeeProfileProps {
  userId: string | null;
  isOpen: boolean;
  onClose: () => void;
  readOnly?: boolean;
}

export function EmployeeProfile({ userId, isOpen, onClose, readOnly = false }: EmployeeProfileProps) {
  const [profiles, setProfiles] = useKV<EmployeeProfileExtended[]>("core-hr-profiles", []);
  const [profile, setProfile] = useState<EmployeeProfileExtended | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<EmployeeProfileExtended>>({});

  useEffect(() => {
    if (userId && isOpen) {
      const found = profiles.find(p => p.userId === userId);
      if (found) {
        setProfile(found);
        setFormData(found);
      } else {
        // Create mock/empty profile if not found
        const newProfile: EmployeeProfileExtended = {
          id: crypto.randomUUID(),
          employeeId: `EMP-${Math.floor(Math.random() * 1000)}`,
          userId: userId,
          personalInfo: { firstName: "", lastName: "", personalEmail: "" },
          workInfo: { department: "", designation: "", dateOfJoining: new Date().toISOString(), employmentType: 'Full-time', employeeStatus: 'Active' },
          contactInfo: {},
          emergencyContacts: [],
          documents: [],
          assets: []
        };
        setProfile(newProfile);
        setFormData(newProfile);
      }
    }
  }, [userId, isOpen, profiles]);

  const handleSave = () => {
    if (!profile) return;
    
    const updatedProfile = { ...profile, ...formData } as EmployeeProfileExtended;
    const existingIndex = profiles.findIndex(p => p.userId === userId);
    
    let newProfiles;
    if (existingIndex >= 0) {
      newProfiles = [...profiles];
      newProfiles[existingIndex] = updatedProfile;
    } else {
      newProfiles = [...profiles, updatedProfile];
    }
    
    setProfiles(newProfiles);
    setProfile(updatedProfile);
    setIsEditing(false);
    toast.success("Profile updated successfully");
  };

  if (!profile) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarFallback>{profile.personalInfo.firstName?.[0]}{profile.personalInfo.lastName?.[0]}</AvatarFallback>
              </Avatar>
              <div>
                <DialogTitle className="text-2xl">{profile.personalInfo.firstName} {profile.personalInfo.lastName}</DialogTitle>
                <DialogDescription>{profile.workInfo.designation} • {profile.workInfo.department}</DialogDescription>
              </div>
            </div>
            {!readOnly && (
              <Button onClick={() => isEditing ? handleSave() : setIsEditing(true)}>
                {isEditing ? <><Check className="mr-2" /> Save Changes</> : "Edit Profile"}
              </Button>
            )}
          </div>
        </DialogHeader>

        <Tabs defaultValue="personal" className="mt-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="personal"><User className="mr-2" /> Personal</TabsTrigger>
            <TabsTrigger value="work"><Briefcase className="mr-2" /> Work</TabsTrigger>
            <TabsTrigger value="contact"><House className="mr-2" /> Contact</TabsTrigger>
            <TabsTrigger value="banking"><Bank className="mr-2" /> Banking</TabsTrigger>
            <TabsTrigger value="documents"><FileText className="mr-2" /> Docs</TabsTrigger>
          </TabsList>

          <TabsContent value="personal" className="space-y-4 py-4">
            <Card>
              <CardHeader><CardTitle>Personal Information</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input 
                    disabled={!isEditing} 
                    value={formData.personalInfo?.firstName || ''} 
                    onChange={e => setFormData({...formData, personalInfo: {...formData.personalInfo!, firstName: e.target.value}})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input 
                    disabled={!isEditing} 
                    value={formData.personalInfo?.lastName || ''}
                    onChange={e => setFormData({...formData, personalInfo: {...formData.personalInfo!, lastName: e.target.value}})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input 
                    type="date" 
                    disabled={!isEditing} 
                    value={formData.personalInfo?.dateOfBirth || ''}
                    onChange={e => setFormData({...formData, personalInfo: {...formData.personalInfo!, dateOfBirth: e.target.value}})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <Select 
                    disabled={!isEditing} 
                    value={formData.personalInfo?.gender}
                    onValueChange={v => setFormData({...formData, personalInfo: {...formData.personalInfo!, gender: v as any}})}
                  >
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Marital Status</Label>
                  <Select 
                    disabled={!isEditing}
                    value={formData.personalInfo?.maritalStatus}
                    onValueChange={v => setFormData({...formData, personalInfo: {...formData.personalInfo!, maritalStatus: v as any}})}
                  >
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single">Single</SelectItem>
                      <SelectItem value="Married">Married</SelectItem>
                      <SelectItem value="Divorced">Divorced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Personal Email</Label>
                  <Input 
                    disabled={!isEditing} 
                    value={formData.personalInfo?.personalEmail || ''}
                    onChange={e => setFormData({...formData, personalInfo: {...formData.personalInfo!, personalEmail: e.target.value}})}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="work" className="space-y-4 py-4">
            <Card>
              <CardHeader><CardTitle>Employment Details</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee ID</Label>
                  <Input disabled value={formData.employeeId} />
                </div>
                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input 
                    disabled={!isEditing} 
                    value={formData.workInfo?.designation || ''}
                    onChange={e => setFormData({...formData, workInfo: {...formData.workInfo!, designation: e.target.value}})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Input 
                    disabled={!isEditing} 
                    value={formData.workInfo?.department || ''}
                    onChange={e => setFormData({...formData, workInfo: {...formData.workInfo!, department: e.target.value}})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date of Joining</Label>
                  <Input 
                    type="date"
                    disabled={!isEditing} 
                    value={formData.workInfo?.dateOfJoining?.split('T')[0] || ''}
                    onChange={e => setFormData({...formData, workInfo: {...formData.workInfo!, dateOfJoining: e.target.value}})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    disabled={!isEditing}
                    value={formData.workInfo?.employeeStatus}
                    onValueChange={v => setFormData({...formData, workInfo: {...formData.workInfo!, employeeStatus: v as any}})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Probation">Probation</SelectItem>
                      <SelectItem value="Notice Period">Notice Period</SelectItem>
                      <SelectItem value="Terminated">Terminated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Add other tabs content as placeholders for now to save tokens/time */}
          <TabsContent value="contact" className="py-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground">Contact details form placeholder.</p>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="banking" className="py-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground">Banking details form placeholder.</p>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="documents" className="py-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground">Document list placeholder.</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
